import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useToast } from '../hooks/useToast';
import { getUserProfile, updateUserProfile, changePassword } from '../api/profile';
import { PageContainer } from '../components/PageContainer';

interface ProfileFormData {
  agencyName: string;
  agencyContactName: string;
  agencyContactPhone: string;
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

export const Profile = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { register, handleSubmit, reset } = useForm<ProfileFormData>();
  const { toast } = useToast();

  useEffect(() => {
    const loadProfile = async () => {
      try {
        console.log('Loading user profile...');
        const profile = await getUserProfile();
        console.log('Profile loaded successfully');
        reset({
          agencyName: profile.agencyName,
          agencyContactName: profile.agencyContactName,
          agencyContactPhone: profile.agencyContactPhone
        });
      } catch (error) {
        console.error('Error loading profile:', error);
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [reset, toast]);

  const onSubmit = async (data: ProfileFormData) => {
    try {
      console.log('Submitting profile update...');
      await updateUserProfile(data);
      console.log('Profile updated successfully');
      toast({
        title: "Success",
        description: "Profile updated successfully",
        variant: "default"
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const onChangePassword = async (data: ProfileFormData) => {
    try {
      if (!data.currentPassword || !data.newPassword || !data.confirmPassword) {
        toast({
          title: "Error",
          description: "All password fields are required",
          variant: "destructive"
        });
        return;
      }

      if (data.newPassword !== data.confirmPassword) {
        toast({
          title: "Error",
          description: "New passwords do not match",
          variant: "destructive"
        });
        return;
      }

      await changePassword(data.currentPassword, data.newPassword);

      // Clear password fields
      reset({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      toast({
        title: "Success",
        description: "Password changed successfully",
        variant: "default"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading profile...</div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Profile Settings</h1>
          <p className="text-muted-foreground">
            Manage your agency profile information
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Agency Name
            </label>
            <input
              {...register('agencyName')}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              placeholder="Enter agency name"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Contact Name
            </label>
            <input
              {...register('agencyContactName')}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              placeholder="Enter contact name"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Contact Phone
            </label>
            <input
              {...register('agencyContactPhone')}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              placeholder="Enter contact phone"
            />
          </div>

          <button
            type="submit"
            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
          >
            Save Changes
          </button>
        </form>

        <div className="mt-8 space-y-6">
          <div>
            <h2 className="text-xl font-bold">Change Password</h2>
            <p className="text-muted-foreground">
              Update your password
            </p>
          </div>

          <form onSubmit={handleSubmit(onChangePassword)} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Current Password
              </label>
              <input
                type="password"
                {...register('currentPassword')}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                placeholder="Enter current password"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                New Password
              </label>
              <input
                type="password"
                {...register('newPassword')}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                placeholder="Enter new password"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Confirm New Password
              </label>
              <input
                type="password"
                {...register('confirmPassword')}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                placeholder="Confirm new password"
              />
            </div>

            <button
              type="submit"
              className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
            >
              Change Password
            </button>
          </form>
        </div>
      </div>
    </PageContainer>
  );
};